/**
 * *********************************************************************************************************************
 * Flexible table component. Allows rendering of expandable rows. any data needed in the child expandable row component
 * is passed through the rows renderData prop. The column prop declares the number of columns in the table and the
 * rows component reflects its rows where each item in the array is one column cell. The name of each row should
 * the same as the column.index item it pertains to. 🐺
 * ****************************************************
 * @format
 */

import React, {useState, cloneElement, useEffect} from 'react';
import PropTypes from 'prop-types';

export const WideTable = props => {
    const {
        rememberExpanded = false,
        defaultExpanded = false,
        className = '',
        isExpandable,
        borderedRows,
        children,
        columns,
        rows,
        ...restOfProps
    } = props;

    const [isExpandedAll, setIsExpandedAll] = useState(defaultExpanded);
    const [isExpanded, setIsExpanded] = useState([]);

    const handleExpandableRow = rowKey => {
        if (isExpanded.includes(rowKey)) {
            setIsExpanded(isExpanded.filter(row => row !== rowKey));
        } else {
            setIsExpanded([...isExpanded, rowKey]);
        }
    };

    const handleExpandCollapseAll = () => {
        if (isExpandedAll) {
            localStorage.setItem('isExpandedAll', 'false');
            setIsExpandedAll(false);
            setIsExpanded([]);
        } else {
            localStorage.setItem('isExpandedAll', 'true');
            setIsExpanded(rows.map(row => row.key));
            setIsExpandedAll(true);
        }
    };

    function checkRememberedExpandedStatus() {
        if (rememberExpanded) {
            const isExpandedAll = localStorage.getItem('isExpandedAll') === 'true';
            if (isExpandedAll) {
                setIsExpanded(rows.map(row => row.key));
                setIsExpandedAll(true);
            } else {
                setIsExpandedAll(false);
                setIsExpanded([]);
            }
        }
    }

    useEffect(() => {
        const expandedRows = defaultExpanded ? rows?.map(row => row.key) : [];
        setIsExpanded(expandedRows);
        checkRememberedExpandedStatus();
    }, [rows, isExpandable]);

    return (
        <div className={`${className} wide-table-common-65gq`} {...restOfProps}>
            <table className="wide-table-common-65gq__table" cellSpacing={0} width={'100%'}>
                <thead>
                    <tr>
                        {columns?.map((column, index) => (
                            <th key={column.key}>
                                <div
                                    className={
                                        'd-flex align-items-center justify-content-start gap-2'
                                    }>
                                    {column.title}
                                    {index === 0 && isExpandable && (
                                        <button
                                            className={
                                                'table-heading-65gq__expand-all d-flex align-items-center justify-content-start gap-1'
                                            }
                                            onClick={handleExpandCollapseAll}>
                                            <i
                                                className={`fa ${
                                                    isExpandedAll ? 'fa-minus' : 'fa-plus'
                                                } color-accent-primary`}
                                            />
                                            <span>all</span>
                                        </button>
                                    )}
                                </div>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className={borderedRows && isExpandable ? 'bordered-rows' : ''}>
                    {rows?.map(row => {
                        const childRowWithData = !children
                            ? children
                            : cloneElement(children, {
                                  ...children.props,
                                  renderData: row.renderData
                              });

                        const rowClass = isExpanded.includes(row.key)
                            ? 'wide-table-common-65gq-table_parent-tr--expanded'
                            : '';

                        const iconClass = isExpanded.includes(row.key) ? 'fa-minus' : 'fa-plus';

                        return (
                            <React.Fragment key={row.key}>
                                <tr className={rowClass}>
                                    {columns.map((column, i) => (
                                        <td key={column.key} {...row.props}>
                                            {isExpandable && i === 0 ? (
                                                <div className="d-flex align-items-center justify-content-start">
                                                    <button
                                                        className="p-0 wide-table-common-65gq-table_expandable-button"
                                                        onClick={() =>
                                                            handleExpandableRow(row.key)
                                                        }>
                                                        <i
                                                            className={`fa ${iconClass} color-accent-primary`}
                                                        />
                                                    </button>
                                                    {row[column.index]}
                                                </div>
                                            ) : (
                                                row[column.index]
                                            )}
                                        </td>
                                    ))}
                                </tr>
                                {isExpanded.includes(row.key) && isExpandable && (
                                    <tr className="wide-table-common-65gq-table_tr--expandable">
                                        <td colSpan={columns.length} className="expandable-65gq_td">
                                            {childRowWithData}
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

WideTable.propTypes = {
    rememberExpanded: PropTypes.bool,
    defaultExpanded: PropTypes.bool,
    className: PropTypes.string,
    isExpandable: PropTypes.bool,
    borderedRows: PropTypes.bool,
    children: PropTypes.node,
    columns: PropTypes.array.isRequired,
    rows: PropTypes.array.isRequired
};
